import { useMemo } from 'react';
import type { Appointment, AvailabilitySlot, AppointmentType } from '../../../mocks/appointments';
import { timeToMinutes } from '../../../utils/occupancy';
import { dayNames } from '../../../mocks/appointments';

interface AdminDaySlotViewProps {
  date: Date;
  slots: AvailabilitySlot[];
  appointments: Appointment[];
  types: AppointmentType[];
  onSelectAppointment?: (appt: Appointment) => void;
  onClose?: () => void;
}

interface TimeFranja {
  start: string;
  end: string;
  maxBookings: number;
  bookedCount: number;
  appointments: Appointment[];
}

export default function AdminDaySlotView({
  date,
  slots,
  appointments,
  types,
  onSelectAppointment,
  onClose,
}: AdminDaySlotViewProps) {
  const iso = useMemo(() => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, [date]);

  const daySlots = useMemo(
    () => slots.filter((s) => s.day_of_week === date.getDay() && s.is_active),
    [slots, date]
  );

  const dayAppointments = useMemo(
    () => appointments.filter((a) => a.preferred_date === iso && a.status !== 'cancelled'),
    [appointments, iso]
  );

  const franjas = useMemo<TimeFranja[]>(() => {
    return daySlots.map((slot) => {
      const bookedAppointments: Appointment[] = [];
      let count = 0;

      dayAppointments.forEach((appt) => {
        if (!appt.preferred_time) return;
        const type = types.find((t) => t.name === appt.service_type);
        const duration = type?.duration_minutes || 30;
        const apptStart = timeToMinutes(appt.preferred_time);
        const apptEnd = apptStart + duration;
        const slotStart = timeToMinutes(slot.start_time);
        const slotEnd = timeToMinutes(slot.end_time);

        if (apptStart < slotEnd && apptEnd > slotStart) {
          count += 1;
          bookedAppointments.push(appt);
        }
      });

      return {
        start: slot.start_time,
        end: slot.end_time,
        maxBookings: slot.max_bookings,
        bookedCount: count,
        appointments: bookedAppointments,
      };
    }).sort((a, b) => timeToMinutes(a.start) - timeToMinutes(b.start));
  }, [daySlots, dayAppointments, types]);

  const totalAvailable = useMemo(() => {
    return franjas.reduce((sum, f) => sum + Math.max(0, f.maxBookings - f.bookedCount), 0);
  }, [franjas]);

  return (
    <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-brand-gray-200">
        <div>
          <h3 className="font-serif text-brand-black text-lg font-medium">
            {dayNames[date.getDay()]} {date.getDate()}
          </h3>
          <p className="font-sans text-brand-gray-500 text-xs mt-0.5">
            {franjas.length} franjas · {totalAvailable} plazas libres
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        )}
      </div>

      {franjas.length === 0 ? (
        <div className="p-8 text-center">
          <p className="font-sans text-brand-gray-400 text-sm">
            No hay franjas configuradas para este día
          </p>
        </div>
      ) : (
        <div className="divide-y divide-brand-gray-200">
          {franjas.map((franja, idx) => {
            const available = Math.max(0, franja.maxBookings - franja.bookedCount);
            const pct = franja.maxBookings > 0 ? (franja.bookedCount / franja.maxBookings) * 100 : 0;
            const isFull = available === 0;
            const isHigh = pct >= 60 && !isFull;

            return (
              <div key={idx} className="px-5 py-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-brand-gray-100">
                      <i className="ri-time-line text-brand-gray-500" />
                    </div>
                    <div>
                      <p className="font-sans text-brand-black text-sm font-medium">
                        {franja.start} — {franja.end}
                      </p>
                      <p className="font-sans text-brand-gray-400 text-xs">
                        Capacidad: {franja.maxBookings} citas
                      </p>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 font-sans text-xs font-medium px-2.5 py-1 rounded-full ${
                      isFull
                        ? 'bg-red-50 text-red-600'
                        : isHigh
                        ? 'bg-amber-50 text-amber-600'
                        : 'bg-emerald-50 text-emerald-600'
                    }`}
                  >
                    {franja.bookedCount} / {franja.maxBookings}
                    {isFull && <i className="ri-close-circle-line" />}
                  </span>
                </div>

                {/* Progress bar */}
                <div className="w-full h-1.5 bg-brand-gray-100 rounded-full overflow-hidden mb-3">
                  <div
                    className={`h-full rounded-full transition-all ${
                      isFull ? 'bg-red-400' : isHigh ? 'bg-amber-400' : 'bg-emerald-400'
                    }`}
                    style={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>

                {/* Appointments in this franja */}
                {franja.appointments.length > 0 ? (
                  <div className="space-y-1.5">
                    {franja.appointments.map((appt) => {
                      const type = types.find((t) => t.name === appt.service_type);
                      const stBg =
                        appt.status === 'pending'
                          ? 'bg-amber-50'
                          : appt.status === 'confirmed'
                          ? 'bg-brand-sage/10'
                          : appt.status === 'completed'
                          ? 'bg-emerald-50'
                          : 'bg-red-50';
                      const stColor =
                        appt.status === 'pending'
                          ? 'text-amber-700'
                          : appt.status === 'confirmed'
                          ? 'text-brand-sage-dark'
                          : appt.status === 'completed'
                          ? 'text-emerald-700'
                          : 'text-red-700';

                      return (
                        <button
                          key={appt.id}
                          onClick={() => onSelectAppointment?.(appt)}
                          className={`w-full text-left rounded-lg px-3 py-2 transition-colors hover:opacity-80 ${stBg}`}
                        >
                          <div className="flex items-center justify-between">
                            <p className="font-sans text-xs font-medium text-brand-black">
                              {appt.preferred_time} — {appt.name}
                            </p>
                            <span className={`font-sans text-[10px] font-medium px-1.5 py-0.5 rounded ${stColor} ${stBg}`}>
                              {appt.status === 'pending'
                                ? 'Pendiente'
                                : appt.status === 'confirmed'
                                ? 'Confirmada'
                                : appt.status === 'completed'
                                ? 'Completada'
                                : 'Cancelada'}
                            </span>
                          </div>
                          <p className="font-sans text-[11px] text-brand-gray-500 mt-0.5 truncate">
                            {appt.service_type}
                            {type?.price && type.price > 0 && ` · €${Number(type.price).toFixed(2)}`}
                            {appt.paid && ' · Pagada'}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="font-sans text-brand-gray-300 text-xs italic">
                    Sin citas en esta franja
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}